import { getOtp, login } from '@/api/api.auth';
import { PrimaryButton } from '@/components/buttons';
import { ResendOtpLink } from '@/components/buttons/resendotp.link.component';
import { Body3, H2, H3 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { AuthContext } from '@/modules/Auth/Auth.context';
import { UserGroup } from '@/modules/Permissions';
import { routes } from '@/modules/Router';
import { useGetRouteParam } from '@/modules/Router/Router.hooks';
import { getIndexRoute } from '@/modules/Router/Router.utils';
import { prepareSlug } from '@/utils/string.utils';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export function LoginForm() {
  const otpRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const navigate = useNavigate();
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const { userGroups, authenticationStatus, setAuthenticationStatus, setUserGroups } = useContext(AuthContext)!;
  const redirectTo = useGetRouteParam('from') || getIndexRoute(userGroups);

  useEffect(() => {
    if (authenticationStatus === 'AUTHENTICATED') {
      navigate(redirectTo);
      return;
    }
  }, [authenticationStatus, navigate, redirectTo]);

  useEffect(() => {
    if (isOtpSent && otpRef.current) {
      otpRef.current.focus();
    }
  }, [isOtpSent]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill out both email and password fields.', {
        autoClose: 3000,
      });
      return;
    }
    setLoading(true);

    try {
      const response = await getOtp(email, password);
      if (response.status === 200 || response.data.succeeded > 0) {
        setIsOtpSent(true);
        toast.success('Passcode sent successfully. Check your linked device.', {
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error((error as Error).message || 'Failed to retrieve passcode. Please try again.', {
        autoClose: 3000,
      });
      console.error('Passcode retrieval failed. Error message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email || !password || !otp) {
        toast.error('Please fill out all fields, including passcode.', {
          autoClose: 3000,
        });
        return;
      }
      setLoading(true);

      try {
        const result = await login(email, password, otp);
        const preparedGroups: UserGroup[] = result.data.groups.map((group: string) => prepareSlug(group));
        setUserGroups(preparedGroups);
        setAuthenticationStatus(result.data.access ? 'AUTHENTICATED' : 'UNAUTHENTICATED');
        toast.success('Login successful. Redirecting...', {
          autoClose: 1000,
        });
        setShouldNavigate(true);
      } catch (error) {
        toast.error((error as Error).message || 'Login failed. Please check your credentials and try again.', {
          autoClose: 3000,
        });
        console.error('Login failed. Error message:', error);
      } finally {
        setLoading(false);
      }
    },
    [otp, password, setAuthenticationStatus, setUserGroups, email]
  );

  if (shouldNavigate) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <>
      <form onSubmit={isOtpSent ? handleLoginSubmit : handleSendOtp} className={cn('flex flex-col gap-6 w-[640px] shadow rounded-lg p-20')}>
        <H2 className={cn('text-center')}>Login</H2>
        {isOtpSent ? (
          <div className={cn('flex flex-col gap-3')}>
            <H3 className={cn('text-center')}>Step 2: Submit Passcode</H3>
            <Body3 className={cn('text-neutral-500 w-full text-center')}>
              Enter your email, password, and passcode below to sign back in.
            </Body3>
          </div>
        ) : (
          <div className={cn('flex flex-col gap-3')}>
            <H3 className={cn('text-center')}>Step 1: Get Passcode</H3>
            <Body3 className={cn('text-neutral-500 w-full text-center')}>
              Enter your email and password to receive the passcode on your linked device.
            </Body3>
          </div>
        )}
        <div>
          <label htmlFor="email" className={cn('block text-sm font-medium text-gray-700')}>
            Email
          </label>
          <input
            type="text"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={cn(
              'mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            )}
            placeholder="Enter your email"
          />
        </div>
        <div>
          <label htmlFor="password" className={cn('block text-sm font-medium text-gray-700')}>
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={cn(
              'mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            )}
            placeholder="Enter your password"
          />
          <Link className={cn('text-neutral-400 mt-1 text-sm block')} to={routes.forgotPassword}>
            Forgot password?
          </Link>
        </div>

        {isOtpSent && (
          <div>
            <label htmlFor="otp" className={cn('block text-sm font-medium text-gray-700')}>
              Passcode
            </label>
            <input
              ref={otpRef}
              type="text"
              id="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              maxLength={6}
              className={cn(
                'mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
              )}
              placeholder="Enter your passcode"
            />
            <ResendOtpLink cooldown={30} email={email} password={password} />
          </div>
        )}
        {isOtpSent ? (
          <PrimaryButton type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </PrimaryButton>
        ) : (
          <PrimaryButton type="submit" disabled={loading}>
            {loading ? 'Sending Passcode...' : 'Get Passcode'}
          </PrimaryButton>
        )}
      </form>
    </>
  );
}
